import React, { useState, useRef } from "react";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import { EstimateMatchCriteria, LineItemOrderObject } from "../../../types";

export interface LineItemOrderTableProps {
	matchCriteria: EstimateMatchCriteria;
	lineItemOrderObjects: LineItemOrderObject[];
	updateLineItemOrderObjects: (lineItemOrders: LineItemOrderObject[]) => Promise<void>;
	lineItems: IDatabaseLineItem[];
	catByLineItemId?: Map<string, string>;
	selByLineItemId?: Map<string, string>;
}

interface ConfirmDropData {
	draggedItems: LineItemOrderObject[];
	targetItem: LineItemOrderObject;
}

const LineItemOrderTable: React.FC<LineItemOrderTableProps> = ({
	matchCriteria,
	lineItemOrderObjects,
	updateLineItemOrderObjects,
	lineItems,
	catByLineItemId,
	selByLineItemId,
}) => {
	const [draggedItems, setDraggedItems] = useState<LineItemOrderObject[]>([]);
	const [confirmDropData, setConfirmDropData] = useState<ConfirmDropData | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const lastSelectedIdRef = useRef<string | null>(null);

	// ── Selection ──────────────────────────────────────────────────────────

	const handleRowClick = (e: React.MouseEvent, item: LineItemOrderObject) => {
		const id = item.lineItemId;

		if (e.shiftKey && lastSelectedIdRef.current) {
			const lastIdx = lineItemOrderObjects.findIndex((i) => i.lineItemId === lastSelectedIdRef.current);
			const currentIdx = lineItemOrderObjects.findIndex((i) => i.lineItemId === id);
			const [start, end] = [Math.min(lastIdx, currentIdx), Math.max(lastIdx, currentIdx)];
			const rangeIds = lineItemOrderObjects.slice(start, end + 1).map((i) => i.lineItemId);
			setSelectedIds((prev) => new Set([...Array.from(prev), ...rangeIds]));
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			lastSelectedIdRef.current = id;
		}
	};

	// ── Drag handlers ──────────────────────────────────────────────────────

	const handleDragStart = (e: React.DragEvent, item: LineItemOrderObject) => {
		const items = selectedIds.has(item.lineItemId)
			? lineItemOrderObjects.filter((i) => selectedIds.has(i.lineItemId))
			: [item];
		setDraggedItems(items);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleDrop = (e: React.DragEvent, targetItem: LineItemOrderObject) => {
		e.preventDefault();
		if (!draggedItems.length) return;
		if (draggedItems.some((i) => i.lineItemId === targetItem.lineItemId)) return;
		setConfirmDropData({ draggedItems, targetItem });
	};

	const confirmDrop = async () => {
		if (!confirmDropData) return;

		const { draggedItems: items, targetItem } = confirmDropData;
		const draggedIds = new Set(items.map((i) => i.lineItemId));

		// Remove dragged items, preserving order of the rest
		const remaining = lineItemOrderObjects.filter((i) => !draggedIds.has(i.lineItemId));

		// Find where targetItem lands after removal
		const targetIdx = remaining.findIndex((i) => i.lineItemId === targetItem.lineItemId);

		// Insert dragged items before the target
		remaining.splice(targetIdx, 0, ...items);

		const updatedItems = remaining.map((item, index) => ({ ...item, index }));
		updateLineItemOrderObjects(updatedItems);

		setDraggedItems([]);
		setConfirmDropData(null);
		setSelectedIds(new Set());
	};

	const cancelDrop = () => {
		setConfirmDropData(null);
		setDraggedItems([]);
	};

	const handleDragEnd = () => {
		setDraggedItems([]);
	};

	// ── Helpers ────────────────────────────────────────────────────────────

	const getLineItemById = (lineItemId: string): IDatabaseLineItem | undefined => {
		return lineItems?.find((item) => item.id === lineItemId);
	};

	if (!lineItemOrderObjects.length) {
		return <div className="text-gray-600">No line items found for this combination of match criteria.</div>;
	}

	const draggedIds = new Set(draggedItems.map((i) => i.lineItemId));

	return (
		<>
			{selectedIds.size > 0 && (
				<div className="mb-3 flex items-center gap-3 text-sm text-blue-700">
					<span>{selectedIds.size} item{selectedIds.size > 1 ? "s" : ""} selected</span>
					<button onClick={() => setSelectedIds(new Set())} className="text-xs underline hover:no-underline">
						Clear selection
					</button>
				</div>
			)}

			<div className="bg-white rounded-lg shadow">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Index</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Item</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{lineItemOrderObjects.map((orderItem) => {
							const lineItem = getLineItemById(orderItem.lineItemId);
							const isBeingDragged = draggedIds.has(orderItem.lineItemId);
							const isSelected = selectedIds.has(orderItem.lineItemId);
							return (
								<tr
									key={orderItem.lineItemId}
									draggable
									onDragStart={(e) => handleDragStart(e, orderItem)}
									onDragOver={handleDragOver}
									onDrop={(e) => handleDrop(e, orderItem)}
									onDragEnd={handleDragEnd}
									onClick={(e) => handleRowClick(e, orderItem)}
									className={`cursor-move transition-colors select-none ${isBeingDragged ? "opacity-50" : ""} ${
										isSelected ? "bg-blue-50 border-l-2 border-blue-400" : "hover:bg-gray-50"
									}`}
								>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{orderItem.index}</td>
									<td className="px-6 py-4 text-sm text-gray-900">
										<div className="flex items-center gap-2">
											<span>{lineItem?.originalLineItem || orderItem.lineItemId}</span>
											{catByLineItemId?.get(orderItem.lineItemId) && (
												<span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
													{catByLineItemId.get(orderItem.lineItemId)}
												</span>
											)}
											{selByLineItemId?.get(orderItem.lineItemId) && (
												<span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
													{selByLineItemId.get(orderItem.lineItemId)}
												</span>
											)}
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Confirmation Modal */}
			{confirmDropData && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Drop</h3>
						<p className="text-gray-700 mb-6">
							Move{" "}
							{confirmDropData.draggedItems.length === 1 ? (
								<span className="font-semibold">
									"{getLineItemById(confirmDropData.draggedItems[0].lineItemId)?.originalLineItem || confirmDropData.draggedItems[0].lineItemId}"
								</span>
							) : (
								<span className="font-semibold">{confirmDropData.draggedItems.length} items</span>
							)}{" "}
							right above{" "}
							<span className="font-semibold">
								"{getLineItemById(confirmDropData.targetItem.lineItemId)?.originalLineItem || confirmDropData.targetItem.lineItemId}"
							</span>
							?
						</p>
						<div className="flex justify-end gap-3">
							<button onClick={cancelDrop} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">
								Cancel
							</button>
							<button onClick={confirmDrop} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
								Confirm
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default LineItemOrderTable;
